import classNames from 'classnames';
import { observer } from 'mobx-react';

import styles from './ReportPage.module.scss';

import { ReportPageAttributes } from 'chaika-test-attributes';

import { DatePickerNames } from './constants';

import { useHandlers } from './hooks/useHandlers';
import { Select } from '@components/Select';
import { MultiSelect } from '@components/Select/MultiSelect';
import { DatePicker } from '@components/datePickers';
import { Button, ButtonModifiers } from '@components/Button';

import { useOptions } from './hooks/useOptions';
import { translate } from '@chaika/i18n/helper';
import { store } from '@store';

export const ReportPage = observer(() => {
    const {
        viewPort: { isMobileSize },
    } = store;
    const {
        maxDate,
        openToDate,
        dateParams,
        selectedLegalEntity,
        selectedOrganizations,
        selectedReportTypes,
        organizationsWithoutStore,
        onCreateReportClick,
        onChangePeriod,
        onSelectLegalEntity,
        onSelectOrganizations,
        onSelectReportType,
    } = useHandlers();
    const { reportTypeOptions, legalEntityOptions, organizationOptions } = useOptions(selectedLegalEntity);

    const { from, to } = dateParams;
    const isCreateReportButtonDisabled = !(
        selectedLegalEntity &&
        selectedReportTypes &&
        selectedOrganizations &&
        selectedOrganizations.length !== 0
    );

    return (
        <div className={styles.root}>
            <div className={styles.report}>
                <div className={styles.controls}>
                    <div className={styles.field}>
                        <div>{translate('period')}</div>
                        <div className={styles.controls}>
                            <DatePicker
                                dataTest={ReportPageAttributes.FromDate}
                                title={translate('from')}
                                name={DatePickerNames.From}
                                maxDate={maxDate}
                                placeholder={translate('ddmmyyyy')}
                                selected={from}
                                onChange={onChangePeriod}
                            />
                            <DatePicker
                                dataTest={ReportPageAttributes.ToDate}
                                title={translate('before')}
                                name={DatePickerNames.To}
                                placeholder={translate('ddmmyyyy')}
                                maxDate={maxDate}
                                selected={to}
                                openToDate={openToDate}
                                onChange={onChangePeriod}
                            />
                        </div>
                    </div>
                    <div className={classNames([styles.field, styles.largeField])}>
                        <div>
                            {translate('makeSelect')} {translate('reportType')}
                        </div>
                        <MultiSelect
                            placeholder={translate('reportType')}
                            dataTest={ReportPageAttributes.LegalEntitySelect}
                            value={selectedReportTypes ?? undefined}
                            options={reportTypeOptions}
                            onChange={onSelectReportType}
                        />
                    </div>
                </div>
                <div className={styles.controls}>
                    <div className={styles.field}>
                        <div>{translate('legalEntity')}</div>
                        <Select
                            placeholder={`${translate('makeSelect')} ${translate('legalEntity')}`}
                            dataTest={ReportPageAttributes.LegalEntitySelect}
                            value={selectedLegalEntity}
                            options={legalEntityOptions}
                            onChange={onSelectLegalEntity}
                        />
                    </div>
                    <div className={classNames([styles.field, styles.largeField])}>
                        <div>{translate('organizations')}</div>
                        <MultiSelect
                            disabled={!selectedLegalEntity}
                            placeholder={`${translate('makeSelect')} ${translate('organizations')}`}
                            dataTest={ReportPageAttributes.OrganizationsSelect}
                            options={organizationOptions}
                            value={selectedOrganizations ?? undefined}
                            onChange={onSelectOrganizations}
                        />
                        {organizationsWithoutStore && organizationsWithoutStore.length > 0 && (
                            <div className={styles.warning}>
                                {`${translate('hasOrganizationWithoutStoreId')}: ${organizationsWithoutStore.join(
                                    ', ',
                                )}`}
                            </div>
                        )}
                    </div>
                </div>
                <div className={styles.createButton}>
                    <Button
                        disabled={isCreateReportButtonDisabled}
                        dataTest={ReportPageAttributes.CreateReportButton}
                        onClick={onCreateReportClick}
                        modifiers={isMobileSize ? [ButtonModifiers.InheritWidth] : undefined}
                    >
                        {translate('generateReport')}
                    </Button>
                </div>
            </div>
        </div>
    );
});
